import * as React from "react";

import {
  Button,
  Center,
  Divider,
  Group,
  HoverCard,
  Stack,
  Switch,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { PDFViewer } from "@react-pdf/renderer";
import { IconQuestionMark, IconDownload } from "@tabler/icons-react";

import { useCountEvent } from "../../../hooks/useCountEvent";
import { mergeStylesWithDefaults, useDocumentStyles } from "../../../hooks/useDocumentStyles";
import { useDownloadPDF } from "../../../hooks/useDownloadPDF";
import { useCurrentEditorDocument } from "../../../hooks/useEditorState";
import { SerializedChecklistDocument } from "../../../lib/model/checklistSerialized";
import { defaultEditorOptions } from "../../../lib/model/documentOptions";
import { newChecklistDocument } from "../../../lib/state/checklist";
import { serializedAppState, serializedChecklistDocument } from "../../../lib/state/convert";
import { store } from "../../../lib/state/store";
import { LivePDFPreview } from "../softpdf/LivePDFPreview";

import { ChecklistDocumentPDF } from "./ChecklistPDF";

export const PDFPreview = (): JSX.Element => {
  const [showPdf, setShowPdf] = React.useState<boolean>(false);
  const { download: downloadDocumentPdf, isLoading } = useDownloadPDF();
  const countEvent = useCountEvent();

  const downloadAction = React.useCallback(() => {
    if (isLoading) return;

    const editingIndex = store.getState().library.editingEntry;
    if (editingIndex !== null) {
      const currentDocument = store.getState().library.entries[editingIndex].present;
      const serializedDocument = serializedChecklistDocument(currentDocument);
      downloadDocumentPdf(serializedDocument);
      countEvent("download-pdf");
    }
  }, [isLoading, downloadDocumentPdf, countEvent]);

  return (
    <Stack align="stretch" pt={"md"} sx={{ height: "100%" }}>
      <Center>
        <Group align="center" spacing="xl">
          <Button size="xs" leftIcon={<IconDownload size={18} />} onClick={downloadAction} loading={isLoading}>
            Download PDF
          </Button>
          <Divider orientation="vertical" />
          <Group align="center" spacing={"xs"}>
            <Switch
              checked={showPdf}
              onChange={(e) => setShowPdf(e.currentTarget.checked)}
              label={<Text>Preview Generated PDF</Text>}
            />
            <HoverCard
              width={280}
              openDelay={500}
              withArrow
              position="top"
              styles={(theme) => ({
                arrow: {
                  backgroundColor: theme.colors.gray[1],
                  border: `1px solid ${theme.colors.dark[1]}`,
                },
                dropdown: {
                  backgroundColor: theme.colors.gray[1],
                  border: `1px solid ${theme.colors.dark[1]}`,
                },
              })}
            >
              <HoverCard.Target>
                <ThemeIcon size="md" variant="light" radius="xl">
                  <IconQuestionMark size={14} stroke={3} />
                </ThemeIcon>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Text size="sm">
                  Lets you display the PDF as it would look when you download it. This option can
                  cause poor performance when used on larger checklists.
                </Text>
              </HoverCard.Dropdown>
            </HoverCard>
          </Group>
        </Group>
      </Center>
      {showPdf ? <HardEditorPDFPreview /> : <LivePDFPreview />}
    </Stack>
  );
};

const HardEditorPDFPreview = (): JSX.Element => {
  const editorDocument = useCurrentEditorDocument(
    (state) => state.contents,
    newChecklistDocument().contents
  );
  const layout = useCurrentEditorDocument((state) => state.layout, "twoColumn");
  const options = useCurrentEditorDocument((state) => state.options, defaultEditorOptions);
  const editorStyles = useDocumentStyles();

  const serializedChecklistDocument = serializedAppState(editorDocument);
  return (
    <PDFViewer width="100%" height="100%" className="pdf-preview" showToolbar={false}>
      <ChecklistDocumentPDF
        checklistDocument={serializedChecklistDocument}
        checklistStyles={editorStyles}
        layout={layout}
        options={options}
      />
    </PDFViewer>
  );
};

export const HardPDFPreview = ({
  document,
}: {
  document: SerializedChecklistDocument;
}): JSX.Element => {
  return (
    <PDFViewer width="100%" height="100%" className="pdf-preview" showToolbar={false}>
      <ChecklistDocumentPDF
        checklistDocument={document.contents}
        checklistStyles={mergeStylesWithDefaults(document.styles)}
        layout={document.layout}
        options={document.options}
      />
    </PDFViewer>
  );
};
